import { InfoToggletip } from "widgets";

import { pluralizeWord } from "@/app-utils/string.ts";
import {
  ContextualFileFilter,
  type ContextualFileFilterOption,
  type FileSuggestionsContextProps,
} from "@/modules/files/stores/file-search.ts";
import { getPlaybookEventName } from "@/redux/modules/event.ts";

export function getSuggestionTooltip(
  key: ContextualFileFilter,
  suggestionsContext?: Partial<FileSuggestionsContextProps>,
) {
  if (!suggestionsContext) {
    return;
  }
  let entityTypes: string;
  let entitiesListing: string[];
  switch (key) {
    case ContextualFileFilter.ASSOCIATED_GENOMES:
      entityTypes = "genomes";
      entitiesListing = (suggestionsContext?.organisms ?? []).map((o) => o.name + " (Organism)");
      break;
    case ContextualFileFilter.ASSOCIATED_PARENTS:
      entityTypes = "parents";
      entitiesListing = (suggestionsContext.parents ?? []).map((p) => p.name + ` (${p.parentType?.name ?? "Unknown"})`);
      break;
    case ContextualFileFilter.ASSOCIATED_SAMPLES:
      entityTypes = "samples";
      entitiesListing = (suggestionsContext.samples ?? []).map((s) => s.name + ` (${s.type?.name ?? "Unknown"})`);
      break;
    case ContextualFileFilter.ASSOCIATED_EVENTS:
      entityTypes = "events";
      entitiesListing = (suggestionsContext.events ?? []).map((e) => getPlaybookEventName(e) + ` (${e.playbook.name})`);
      break;
  }

  // truncate the listing of associated entities
  const entitiesListingCount = entitiesListing.length;
  if (entitiesListingCount > 15) {
    entitiesListing = entitiesListing.slice(0, 10);
    entitiesListing.push(`...and ${entitiesListingCount - 10} others`);
  }

  return (
    <>
      Enabling this filter will search for files already associated to the {entityTypes} that we have detected from the
      currently loaded data: <br />{" "}
      {entitiesListing.map((item, index) => (
        <div key={index}>- {item}</div>
      ))}
    </>
  );
}

export function getContextualFileFilterOptions(
  suggestionsContext?: Partial<FileSuggestionsContextProps>,
): ContextualFileFilterOption[] {
  const genomesCount = suggestionsContext?.organisms?.length ?? 0;
  const samplesCount = suggestionsContext?.samples?.length ?? 0;
  const parentsCount = suggestionsContext?.parents?.length ?? 0;
  const eventsCount = suggestionsContext?.events?.length ?? 0;

  return [
    {
      key: ContextualFileFilter.ASSOCIATED_GENOMES,
      label: (
        <>
          Associated organisms
          <InfoToggletip content={getSuggestionTooltip(ContextualFileFilter.ASSOCIATED_GENOMES, suggestionsContext)} />
        </>
      ),
      active: !!genomesCount,
      description: `${genomesCount ? `${genomesCount} ${pluralizeWord("organism", genomesCount)}` : ""} found`,
    },
    {
      key: ContextualFileFilter.ASSOCIATED_EVENTS,
      label: (
        <>
          Associated events
          <InfoToggletip content={getSuggestionTooltip(ContextualFileFilter.ASSOCIATED_EVENTS, suggestionsContext)} />
        </>
      ),
      active: !!suggestionsContext?.events?.length,
      description: `${eventsCount} ${pluralizeWord("event", eventsCount)} found`,
    },
    {
      key: ContextualFileFilter.ASSOCIATED_SAMPLES,
      label: (
        <>
          Associated samples
          <InfoToggletip content={getSuggestionTooltip(ContextualFileFilter.ASSOCIATED_SAMPLES, suggestionsContext)} />
        </>
      ),
      active: !!suggestionsContext?.samples?.length,
      description: `${samplesCount} ${pluralizeWord("sample", samplesCount)} found`,
    },
    {
      key: ContextualFileFilter.ASSOCIATED_PARENTS,
      label: (
        <>
          Associated {suggestionsContext?.parents?.[0]?.parentType?.name ?? "Unknown"}s
          <InfoToggletip content={getSuggestionTooltip(ContextualFileFilter.ASSOCIATED_PARENTS, suggestionsContext)} />
        </>
      ),
      active: !!suggestionsContext?.parents?.length,
      description: `${parentsCount} ${pluralizeWord("parent", parentsCount)} found`,
    },
  ];
}
